import React from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  Chip,
  Alert,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { get } from '../../utils/api';
import {
  CloseX,
  ignoreBackdropEscapeClose,
  portalTableContainerSx,
  portalTableHeadCellSx,
  portalTableBodyRowSx,
} from '../../components/portal/portalUi';
import PortalPageHeader from '../../components/portal/PortalPageHeader';

function pledgeStatusKey(status) {
  return String(status || '').toLowerCase().trim();
}

function pledgeBalanceNum(o) {
  return Number(o?.balance_total ?? 0);
}

/** Woo status completed, or no remaining balance (mirror may still say pledge-pending). */
function isPledgeComplete(o) {
  if (!o) return false;
  if (pledgeStatusKey(o.status) === 'pledge-completed') return true;
  if (pledgeBalanceNum(o) <= 0) return true;
  return false;
}

const DEFAULT_PLEDGE_PAY_URL = 'https://icctmemphis.org/store/products/';

function resolvePledgePayUrl(o) {
  const u = String(o?.pay_url || '').trim();
  if (u) return u;
  return DEFAULT_PLEDGE_PAY_URL;
}

function formatPledgeStatusLabel(o) {
  if (isPledgeComplete(o)) return 'Completed';
  const s = pledgeStatusKey(o?.status);
  if (s === 'pledge-pending') return 'Pending';
  return o?.status ? String(o.status) : '—';
}

function statusChipColor(o) {
  if (isPledgeComplete(o)) return 'success';
  const s = pledgeStatusKey(o?.status);
  if (s === 'pledge-pending') return 'warning';
  return 'default';
}

/** Pending first (newest first), then completed (newest first). */
function sortPledgesForDisplay(list) {
  const pending = list.filter((o) => !isPledgeComplete(o));
  const completed = list.filter((o) => isPledgeComplete(o));
  const byDateDesc = (a, b) => {
    const ta = new Date(a.date_created_gmt || a.date_created || 0).getTime();
    const tb = new Date(b.date_created_gmt || b.date_created || 0).getTime();
    return tb - ta;
  };
  pending.sort(byDateDesc);
  completed.sort(byDateDesc);
  return [...pending, ...completed];
}

function filterPledgesByToggle(ordered, filter) {
  if (filter === 'pending') return ordered.filter((o) => !isPledgeComplete(o));
  if (filter === 'completed') return ordered.filter((o) => isPledgeComplete(o));
  return ordered;
}

function pledgeDisplayName(o) {
  const items = Array.isArray(o.line_items) ? o.line_items : [];
  if (items.length === 0) return 'Pledge';
  return items
    .map((li) => {
      const q = li.quantity && Number(li.quantity) > 1 ? ` ×${Number(li.quantity)}` : '';
      return `${li.name || 'Item'}${q}`;
    })
    .join('; ');
}

function formatMoney(n) {
  const x = Number(n || 0);
  return `$${x.toFixed(2)}`;
}

function formatPledgeDateOnly(d) {
  try {
    return new Date(d).toLocaleDateString();
  } catch {
    return '—';
  }
}

function PledgeDetailCol({ label, children, sx: sxProp }) {
  return (
    <Box sx={{ flex: '0 0 auto', minWidth: 0, ...sxProp }}>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ lineHeight: 1.2 }}>
        {label}
      </Typography>
      <Box sx={{ mt: 0.5 }}>{children}</Box>
    </Box>
  );
}

export default function PortalPledgesPage() {
  const { staff } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [pledgesLoading, setPledgesLoading] = React.useState(true);
  const [pledgesError, setPledgesError] = React.useState('');
  const [pledges, setPledges] = React.useState([]);
  const [statusFilter, setStatusFilter] = React.useState('all');

  const [detailOpen, setDetailOpen] = React.useState(false);
  const [detailPledge, setDetailPledge] = React.useState(null);

  const [payConfirmOpen, setPayConfirmOpen] = React.useState(false);
  const [payConfirmUrl, setPayConfirmUrl] = React.useState('');
  const [payPopupBlocked, setPayPopupBlocked] = React.useState(false);

  React.useEffect(() => {
    if (!staff?.subject) return;
    let cancelled = false;
    (async () => {
      setPledgesLoading(true);
      setPledgesError('');
      try {
        const res = await get('/portal/pledge-totals/by-customer', {});
        if (cancelled) return;
        setPledges(Array.isArray(res?.data) ? res.data : []);
      } catch (e) {
        if (!cancelled) setPledgesError(e?.message || 'Failed to load pledges');
      } finally {
        if (!cancelled) setPledgesLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [staff?.subject]);

  const orderedPledges = React.useMemo(() => sortPledgesForDisplay(pledges), [pledges]);
  const visiblePledges = React.useMemo(
    () => filterPledgesByToggle(orderedPledges, statusFilter),
    [orderedPledges, statusFilter]
  );

  const summary = React.useMemo(() => {
    let pledged = 0;
    let paid = 0;
    let balance = 0;
    for (const o of visiblePledges) {
      pledged += Number(o.pledge_total ?? o.total_num ?? 0);
      paid += Number(o.paid_total ?? 0);
      balance += Number(o.balance_total ?? 0);
    }
    return {
      count: visiblePledges.length,
      pledged,
      paid,
      balance,
    };
  }, [visiblePledges]);

  const openDetail = (o) => {
    setDetailPledge(o);
    setDetailOpen(true);
  };

  const closeDetail = () => {
    setDetailOpen(false);
    setDetailPledge(null);
  };

  React.useEffect(() => {
    const openId = Number(location.state?.openPledgeId);
    if (!openId || !Array.isArray(pledges) || pledges.length === 0) return;
    const target = pledges.find((p) => Number(p?.id) === openId);
    if (!target) return;
    setDetailPledge(target);
    setDetailOpen(true);
    navigate(location.pathname, { replace: true, state: {} });
  }, [location.pathname, location.state, navigate, pledges]);

  const openPayConfirm = (url) => {
    const u = String(url || '').trim();
    if (!u) return;
    setPayConfirmUrl(u);
    setPayPopupBlocked(false);
    setPayConfirmOpen(true);
  };

  const closePayConfirm = () => {
    setPayConfirmOpen(false);
    setPayConfirmUrl('');
    setPayPopupBlocked(false);
  };

  const confirmOpenPaymentTab = () => {
    const u = String(payConfirmUrl || '').trim();
    if (!u) return;
    const win = window.open(u, '_blank', 'noopener,noreferrer');
    if (!win) {
      setPayPopupBlocked(true);
      return;
    }
    closePayConfirm();
  };

  return (
    <>
      <PortalPageHeader title="Pledges" />
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Review your pledges, payment history, and pay remaining balances.
      </Typography>

      {pledgesLoading && (
        <Typography variant="body2" color="text.secondary">Loading pledges…</Typography>
      )}
      {pledgesError && (
        <Typography variant="body2" color="error">{pledgesError}</Typography>
      )}

      {!pledgesLoading && !pledgesError && (
        <>
          <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'stretch', sm: 'center' }} justifyContent="space-between" spacing={2} sx={{ mb: 2 }}>
            <ToggleButtonGroup
              exclusive
              size="small"
              value={statusFilter}
              onChange={(_e, v) => v != null && setStatusFilter(v)}
              aria-label="Filter pledges by status"
            >
              <ToggleButton value="all">All</ToggleButton>
              <ToggleButton value="pending">Pending</ToggleButton>
              <ToggleButton value="completed">Completed</ToggleButton>
            </ToggleButtonGroup>
          </Stack>

          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={6} sm={3}>
              <Card variant="outlined" sx={{ height: '100%', borderRadius: 2 }}>
                <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Typography variant="caption" color="text.secondary" display="block">Pledges shown</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>{summary.count}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Card variant="outlined" sx={{ height: '100%', borderRadius: 2, bgcolor: 'rgba(26, 35, 126, 0.04)' }}>
                <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Typography variant="caption" color="text.secondary" display="block">Total pledged</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>{formatMoney(summary.pledged)}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Card variant="outlined" sx={{ height: '100%', borderRadius: 2, bgcolor: 'rgba(46, 125, 50, 0.06)' }}>
                <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Typography variant="caption" color="text.secondary" display="block">Paid</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: 'success.dark' }}>{formatMoney(summary.paid)}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Card variant="outlined" sx={{ height: '100%', borderRadius: 2, bgcolor: 'rgba(237, 108, 2, 0.06)' }}>
                <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Typography variant="caption" color="text.secondary" display="block">Balance</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: summary.balance > 0 ? 'warning.dark' : 'text.primary' }}>
                    {formatMoney(summary.balance)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {pledges.length === 0 ? (
            <Typography variant="body2" color="text.secondary">No pledge records found.</Typography>
          ) : (
            <TableContainer component={Paper} variant="outlined" sx={portalTableContainerSx}>
              <Table size="small" stickyHeader sx={{ minWidth: 720 }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={portalTableHeadCellSx}>Date pledged</TableCell>
                    <TableCell sx={portalTableHeadCellSx}>Pledge #</TableCell>
                    <TableCell sx={portalTableHeadCellSx}>Item</TableCell>
                    <TableCell sx={portalTableHeadCellSx}>Status</TableCell>
                    <TableCell align="right" sx={portalTableHeadCellSx}>Pledged</TableCell>
                    <TableCell align="right" sx={portalTableHeadCellSx}>Paid</TableCell>
                    <TableCell align="right" sx={portalTableHeadCellSx}>Balance</TableCell>
                    <TableCell align="right" sx={portalTableHeadCellSx}>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {visiblePledges.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8}>
                        <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                          No pledges match this filter.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    visiblePledges.map((o, rowIdx) => {
                      const items = Array.isArray(o.line_items) ? o.line_items : [];
                      const pledged = Number(o.pledge_total ?? o.total_num ?? 0);
                      const paid = Number(o.paid_total ?? 0);
                      const balance = Number(o.balance_total ?? 0);
                      const dateStr = new Date(o.date_created_gmt || o.date_created).toLocaleDateString();
                      return (
                        <TableRow key={o.id} hover sx={portalTableBodyRowSx(rowIdx)}>
                          <TableCell>{dateStr}</TableCell>
                          <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>{o.id}</TableCell>
                          <TableCell sx={{ maxWidth: 280 }}>
                            {items.length === 0 ? (
                              <Typography variant="body2">—</Typography>
                            ) : (
                              items.map((li, idx) => (
                                <Typography key={`${o.id}-li-${idx}`} variant="body2" sx={{ lineHeight: 1.45 }}>
                                  {li.name || 'Item'}
                                  {li.quantity && Number(li.quantity) > 1 ? ` ×${Number(li.quantity)}` : ''}
                                </Typography>
                              ))
                            )}
                          </TableCell>
                          <TableCell>
                            <Chip size="small" label={formatPledgeStatusLabel(o)} color={statusChipColor(o)} variant="outlined" />
                          </TableCell>
                          <TableCell align="right">{formatMoney(pledged)}</TableCell>
                          <TableCell align="right">{formatMoney(paid)}</TableCell>
                          <TableCell
                            align="right"
                            sx={{
                              fontWeight: balance > 0 ? 600 : 400,
                              color: balance > 0 ? 'warning.dark' : balance < 0 ? 'error.main' : 'success.dark',
                            }}
                          >
                            {formatMoney(balance)}
                          </TableCell>
                          <TableCell align="right">
                            <Stack direction="row" spacing={0.75} justifyContent="flex-end" flexWrap="wrap" useFlexGap>
                              <Button size="small" variant="outlined" onClick={() => openDetail(o)}>
                                View
                              </Button>
                              {!isPledgeComplete(o) && (
                                <Button
                                  size="small"
                                  variant="contained"
                                  onClick={() => {
                                    if (staff?.subject) {
                                      navigate(`/devotees/${staff.subject}/pledges/${o.id}/pay`);
                                    }
                                  }}
                                >
                                  Pay for pledge
                                </Button>
                              )}
                            </Stack>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}

      <Dialog
        open={detailOpen}
        onClose={(e, reason) => {
          if (ignoreBackdropEscapeClose(e, reason)) return;
          closeDetail();
        }}
        disableEscapeKeyDown
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
          <Typography variant="h6">Pledge details</Typography>
          <CloseX onClick={closeDetail} />
        </DialogTitle>
        <DialogContent dividers>
          {detailPledge && (
            <Stack spacing={2}>
              <Box sx={{ overflowX: 'auto', pb: 0.5 }}>
                <Stack
                  direction="row"
                  alignItems="flex-end"
                  spacing={2}
                  sx={{
                    flexWrap: 'nowrap',
                    minWidth: { xs: 720, sm: '100%' },
                  }}
                >
                  <PledgeDetailCol label="Pledge name" sx={{ flex: '1 1 160px', minWidth: 160, maxWidth: 280 }}>
                    <Typography variant="body2" sx={{ fontWeight: 700, lineHeight: 1.35 }}>
                      {pledgeDisplayName(detailPledge)}
                    </Typography>
                  </PledgeDetailCol>
                  <PledgeDetailCol label="Pledge #">
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{detailPledge.id}</Typography>
                  </PledgeDetailCol>
                  <PledgeDetailCol label="Date pledged">
                    <Typography variant="body2">
                      {formatPledgeDateOnly(detailPledge.date_created_gmt || detailPledge.date_created)}
                    </Typography>
                  </PledgeDetailCol>
                  <PledgeDetailCol label="Status">
                    <Chip size="small" label={formatPledgeStatusLabel(detailPledge)} color={statusChipColor(detailPledge)} />
                  </PledgeDetailCol>
                  <PledgeDetailCol label="Pledge amount">
                    <Typography variant="body1" sx={{ fontWeight: 700 }}>{formatMoney(detailPledge.pledge_total ?? detailPledge.total_num)}</Typography>
                  </PledgeDetailCol>
                  <PledgeDetailCol label="Paid">
                    <Typography variant="body1" sx={{ fontWeight: 700, color: 'success.dark' }}>{formatMoney(detailPledge.paid_total)}</Typography>
                  </PledgeDetailCol>
                  <PledgeDetailCol label="Balance">
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: 700,
                        color: Number(detailPledge.balance_total) > 0
                          ? 'warning.dark'
                          : Number(detailPledge.balance_total) < 0
                            ? 'error.main'
                            : 'success.dark',
                      }}
                    >
                      {formatMoney(detailPledge.balance_total)}
                    </Typography>
                  </PledgeDetailCol>
                  <PledgeDetailCol label="Payment">
                    {!isPledgeComplete(detailPledge) ? (
                      <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        onClick={() => openPayConfirm(resolvePledgePayUrl(detailPledge))}
                        endIcon={<OpenInNewIcon sx={{ fontSize: '1rem !important' }} />}
                      >
                        Pay for pledge
                      </Button>
                    ) : (
                      <Typography variant="caption" color="text.secondary">—</Typography>
                    )}
                  </PledgeDetailCol>
                </Stack>
              </Box>

              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Payments</Typography>
                {(!Array.isArray(detailPledge.transactions) || detailPledge.transactions.length === 0) ? (
                  <Typography variant="body2" color="text.secondary">No completed payments applied to this pledge yet.</Typography>
                ) : (
                  <TableContainer component={Paper} variant="outlined" sx={{ ...portalTableContainerSx, borderRadius: 1 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={portalTableHeadCellSx}>Date</TableCell>
                          <TableCell sx={portalTableHeadCellSx}>Order #</TableCell>
                          <TableCell sx={portalTableHeadCellSx}>Qty</TableCell>
                          <TableCell align="right" sx={portalTableHeadCellSx}>Amount</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {detailPledge.transactions.map((t, i) => (
                          <TableRow key={`${t.orderId}-${i}`} hover sx={portalTableBodyRowSx(i)}>
                            <TableCell>{new Date(t.date).toLocaleDateString()}</TableCell>
                            <TableCell sx={{ fontFamily: 'monospace' }}>{t.orderId}</TableCell>
                            <TableCell>{t.qty}</TableCell>
                            <TableCell align="right">{formatMoney(t.amount)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Box>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDetail}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={payConfirmOpen}
        onClose={(e, reason) => {
          if (ignoreBackdropEscapeClose(e, reason)) return;
          closePayConfirm();
        }}
        disableEscapeKeyDown
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
          <Typography variant="h6" component="span">Open payment page</Typography>
          <CloseX onClick={closePayConfirm} />
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            The ICCT website will open in a <strong>new browser tab</strong> so you can complete payment for this pledge.
            Keep this portal tab open if you want to return here afterward.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            If nothing opens, your browser may have blocked the popup—check the address bar for a blocked-popup icon, or allow popups for this site and try again.
          </Typography>
          {payPopupBlocked && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              The new tab may have been blocked. Try the button again after allowing popups, or open the store using the link below (same as a normal website link).
            </Alert>
          )}
          {payPopupBlocked && payConfirmUrl ? (
            <Button
              component="a"
              href={payConfirmUrl}
              target="_blank"
              rel="noopener noreferrer"
              variant="outlined"
              fullWidth
              endIcon={<OpenInNewIcon />}
              sx={{ mb: 1 }}
            >
              Open payment page (link)
            </Button>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1, flexWrap: 'wrap' }}>
          <Button onClick={closePayConfirm}>Cancel</Button>
          <Button variant="contained" onClick={confirmOpenPaymentTab} endIcon={<OpenInNewIcon />}>
            Open in new tab
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
